import i18next from 'i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import { useMemo, useSyncExternalStore } from 'react'

import {
    AVAILABLE_LANGUAGES,
    FALLBACK_LANGUAGE,
    LANGUAGE_RESOURCE_LOADERS,
    LANGUAGE_STORAGE_KEY,
} from '@/config/language.config.ts'
import type { AppLanguage } from '@/shared/Types/config.types.ts'

const subscribers = new Set<() => void>()
const availableLanguageSet = new Set<string>(AVAILABLE_LANGUAGES)

let resourceVersion = 0

function isAppLanguage(value: string | null | undefined): value is AppLanguage {
    return typeof value === 'string' && availableLanguageSet.has(value)
}

function getStoredLanguage(): AppLanguage {
    if (typeof window === 'undefined') {
        return FALLBACK_LANGUAGE
    }

    const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)

    if (isAppLanguage(storedLanguage)) {
        return storedLanguage
    }

    const browserLanguage = window.navigator.language.split('-')[0]

    if (isAppLanguage(browserLanguage)) {
        return browserLanguage
    }

    return FALLBACK_LANGUAGE
}

export const translationReady = i18next
    .use(
        resourcesToBackend(function (language: string) {
            const loader = isAppLanguage(language)
                ? LANGUAGE_RESOURCE_LOADERS[language]
                : LANGUAGE_RESOURCE_LOADERS[FALLBACK_LANGUAGE]

            return loader()
        }),
    )
    .init({
        lng: getStoredLanguage(),
        fallbackLng: FALLBACK_LANGUAGE,
        supportedLngs: AVAILABLE_LANGUAGES,
        interpolation: {
            escapeValue: false,
        },
    })

function emitChange() {
    subscribers.forEach(function (subscriber) {
        subscriber()
    })
}

function emitResourceChange() {
    resourceVersion += 1
    emitChange()
}

i18next.on('initialized', emitResourceChange)
i18next.on('loaded', emitResourceChange)

i18next.on('languageChanged', emitChange)

function subscribe(subscriber: () => void) {
    subscribers.add(subscriber)

    return function unsubscribe() {
        subscribers.delete(subscriber)
    }
}

function getSnapshot() {
    return `${i18next.resolvedLanguage ?? FALLBACK_LANGUAGE}:${resourceVersion}`
}

function getServerSnapshot() {
    return `${FALLBACK_LANGUAGE}:0`
}

async function setLanguage(language: AppLanguage) {
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    }

    await i18next.changeLanguage(language)
}

export default function useTranslationStore() {
    const snapshot = useSyncExternalStore(
        subscribe,
        getSnapshot,
        getServerSnapshot,
    )
    const storedLanguage = snapshot.split(':')[0]
    const language = isAppLanguage(storedLanguage)
        ? storedLanguage
        : FALLBACK_LANGUAGE
    const t = useMemo(() => i18next.getFixedT(language), [language])

    return {
        availableLanguages: AVAILABLE_LANGUAGES,
        language,
        setLanguage,
        t,
    }
}
